import { Box, Divider, List } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React, { FC } from 'react'
import { useHistory } from 'react-router-dom'
import Loader from 'src/designSystem/Loader'
import DevPanel from 'src/devPanel/DevPanel'
import { useFeatureFlag, useFeatureFlagsLoading } from 'src/featureFlags'
import { isSuccessWithData, useFirebase } from 'src/firebase'
import { EnrollmentState } from 'src/graphql.generated'
import { Program } from 'src/legacy/models/firebase'
import { isLocalDevelopment } from 'src/lib/env'
import { useSessionUID } from 'src/session/useSession'
import { useFetchParticipantsForHomeViewQuery } from './searchParticipants.generated'
import sortParticipants, {
  HOME_VIEW_ENROLLMENT_STATES,
  ONBOARDING_MODULE,
  POST_PROGRAM_THEME,
} from './sortParticipants'
import { Participants } from './Participants'
import ModuleDivider from './ModuleDivider'
import ParticipantListFilters from './ParticipantListFilters'
import ParticipantListControls from './ParticipantListControls'
import PriorityFilters from './PriorityFilters'
import FiltersMenu from './FiltersMenu'
import useParticipantFilters from './useParticipantFilters'
import useParticipantListAnalytics from './useParticipantListAnalytics'
import useLiveParticipantIndicators from './useLiveParticipantIndicators'
import { PrioritySelectionChange, RoleSelectionChange, TagFilterAction } from './participantListAnalytics'
import { participantMatchesPriorityFilters, PriorityFilter } from './priorityFilterUtils'
import { RoleFilter } from './roleFilterUtils'

const useStyles = makeStyles((theme) => ({
  filterContainer: {
    paddingBottom: theme.spacing(1),
  },
  filtersContainer: {
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(1),
  },
  moduleDividerContainer: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
}))

type Props = {
  selectedUid?: string
}

const ParticipantList: FC<Props> = ({ selectedUid }) => {
  const history = useHistory()
  const hasPriorityFilters = useFeatureFlag('featureFlagDashboardPriorityFilters')
  const coachID = useSessionUID()
  const featureFlagsLoading = useFeatureFlagsLoading()
  const { data, loading } = useFetchParticipantsForHomeViewQuery({
    skip: featureFlagsLoading,
    variables: {
      coachIDs: [coachID],
      states: HOME_VIEW_ENROLLMENT_STATES.map((entry) => entry.enrollmentState),
      showPriorityIndicators: hasPriorityFilters,
      showDevInfo: isLocalDevelopment,
    },
  })

  useLiveParticipantIndicators({ coachID, showPriorityIndicators: hasPriorityFilters, enabled: !featureFlagsLoading })

  const allParticipants = data?.searchPatientsV2.patients || []

  const { availableFilterTags, filteredParticipants, filters, priorityCounts, uidsWithDraftMessage, updateFilters } =
    useParticipantFilters(allParticipants, selectedUid, { matchAnyTag: hasPriorityFilters, coachID })

  const selectedParticipant = allParticipants.find((participant) => participant.uid === selectedUid)
  const selectedHasDraftMessage = selectedUid ? uidsWithDraftMessage.has(selectedUid) : false

  const programsRequest = useFirebase<Record<string, Program>>('programs')
  // A refetch reports `loading` but keeps `data`; showing the loader then would unmount the list
  // and lose its scroll position and collapsed groups.
  const isInitialLoad = loading && data === undefined
  const isReady = !featureFlagsLoading && !isInitialLoad && isSuccessWithData(programsRequest)
  const modules = isReady ? sortParticipants(filteredParticipants, programsRequest.data) : []

  // Analytics events all belong to the new triage UI, so they only fire behind the flag. The
  // snapshot they publish is built from this query alone, so it doesn't wait on the programs
  // read and survives a refetch (saving a call note triggers one), where `data` is retained.
  const { trackPrioritiesChanged, trackRolesChanged, trackTagsChanged } = useParticipantListAnalytics({
    enabled: hasPriorityFilters && isReady,
    publishSnapshot: hasPriorityFilters && !featureFlagsLoading && data !== undefined,
    filters,
    allParticipants,
    filteredParticipants,
    uidsWithDraftMessage,
    priorityCounts,
    selectedParticipant,
    selectedHasDraftMessage,
  })

  const handlePrioritiesChange = (priorities: Array<PriorityFilter>, change: PrioritySelectionChange) => {
    trackPrioritiesChanged(priorities, change)
    updateFilters({ priorities })
    // The list keeps the selected participant visible under any group, so one outside the group would linger.
    const selectedIsInGroup =
      selectedParticipant !== undefined &&
      participantMatchesPriorityFilters(selectedParticipant, selectedHasDraftMessage, priorities)
    if (change.selected && selectedUid && !selectedIsInGroup) {
      history.replace('/participant')
    }
  }

  const handleTagsChange = (tags: Array<string>, action: TagFilterAction) => {
    trackTagsChanged(action, tags)
    updateFilters({ tags })
  }

  const handleRolesChange = (roles: Array<RoleFilter>, change: RoleSelectionChange) => {
    trackRolesChanged(roles, change)
    updateFilters({ roles })
  }

  const handleClearFilters = () => {
    if (filters.roles.length > 0) {
      trackRolesChanged([], { action: 'clear' }, [])
    }
    if (filters.tags.length > 0) {
      trackTagsChanged('clear', [], [])
    }
    updateFilters({ roles: [], tags: [] })
  }

  const styles = useStyles()

  const lastModuleKey = modules.length > 0 ? modules[modules.length - 1].moduleKey : null

  return (
    <Box display="flex" style={{ height: '100%' }} flexDirection="column" alignContent="center" mt={1}>
      <Box className={hasPriorityFilters ? styles.filtersContainer : styles.filterContainer}>
        {hasPriorityFilters ? (
          <FiltersMenu
            tags={availableFilterTags}
            selectedTags={filters.tags}
            selectedRoles={filters.roles}
            onTagsChange={handleTagsChange}
            onRolesChange={handleRolesChange}
            onClear={handleClearFilters}
            controls={<ParticipantListControls />}
            priorityFilters={
              <PriorityFilters
                counts={priorityCounts}
                selectedFilters={filters.priorities}
                onChange={handlePrioritiesChange}
              />
            }
          />
        ) : (
          <ParticipantListFilters filters={filters} tags={availableFilterTags} updateFilters={updateFilters} />
        )}
      </Box>
      <Divider />
      {!isReady ? (
        <Loader size="large" />
      ) : (
        <Box overflow="auto" whiteSpace="nowrap">
          <List disablePadding>
            {modules.map((participantModule) => (
              <React.Fragment key={participantModule.moduleKey}>
                <Box className={styles.moduleDividerContainer}>
                  <ModuleDivider label={participantModule.moduleLabel} />
                </Box>
                {participantModule.groups.map((group, groupIndex) => (
                  <Participants
                    key={group.key}
                    participants={group.participants}
                    label={group.label}
                    selectedUid={selectedUid}
                    showDayIndicator={
                      participantModule.moduleKey !== ONBOARDING_MODULE.key &&
                      group.key !== POST_PROGRAM_THEME.key &&
                      group.enrollmentState === EnrollmentState.InProgram
                    }
                    isLastGroup={
                      participantModule.moduleKey === lastModuleKey &&
                      groupIndex === participantModule.groups.length - 1
                    }
                  />
                ))}
              </React.Fragment>
            ))}
          </List>
        </Box>
      )}
      {isLocalDevelopment && selectedUid && <DevPanel uid={selectedUid} />}
    </Box>
  )
}

export default ParticipantList
